<template>
    <div class="season-scene" :class="{'is-new-year': newYear, 'is-still': reduced}">
        <!-- Céu: lua (Natal), neve com bokeh e estrelas piscando; no Réveillon, fogos e confete -->
        <div v-if="!newYear" class="season-moon" aria-hidden="true"></div>
        <canvas v-if="newYear" ref="fireworks" class="season-scene-canvas" aria-hidden="true"></canvas>
        <season-sky v-if="!reduced" :key="newYear ? 'confetti' : 'snow'" :density="(newYear ? 0.9 : 1.15) * intensity * flurry"
                    :max-size="newYear ? 6 : 5.5" :speed="0.9" :confetti="newYear" bokeh :glints="newYear ? 4 : 10"></season-sky>
        <canvas ref="trail" class="season-scene-canvas" aria-hidden="true"></canvas>

        <!-- Trenó dourado com as renas: cruza o céu (passando pela lua) de tempos em tempos -->
        <svg v-if="!newYear" ref="sleigh" class="season-sleigh" viewBox="0 0 300 64" aria-hidden="true">
            <defs>
                <linearGradient id="season-sleigh-gold" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stop-color="#fff4cf"/>
                    <stop offset="0.5" stop-color="#f3d68a"/>
                    <stop offset="1" stop-color="#c8973c"/>
                </linearGradient>
            </defs>
            <g fill="url(#season-sleigh-gold)" stroke="url(#season-sleigh-gold)" stroke-linecap="round" stroke-linejoin="round">
                <!-- Renas -->
                <g v-for="x in [158, 222]" :key="x" :transform="`translate(${x} 0)`" class="season-reindeer">
                    <ellipse cx="20" cy="30" rx="16" ry="6.5" stroke="none"/>
                    <path d="M32 27 L39 15 L47 13 L48 17 L41 19 L36 29 Z" stroke="none"/>
                    <path d="M40 15 L37 6 M38.5 10 L34.5 7 M43 14 L45 5 M44 9 L48.5 6.5" fill="none" stroke-width="1.6"/>
                    <path class="season-reindeer-legs" d="M31 34 L41 41 M29 35 L34 46 M10 34 L1 39 M12 35 L7 46" fill="none" stroke-width="2.4"/>
                    <path d="M4 28 L0 25" fill="none" stroke-width="2"/>
                </g>
                <!-- Rédeas -->
                <path d="M112 30 Q140 22 160 28 M182 27 Q200 24 224 28" fill="none" stroke-width="0.9" opacity="0.8"/>
                <!-- Saco de presentes, Papai Noel e trenó -->
                <path d="M48 31 Q44 13 58 14 Q69 16 65 33 Z" stroke="none" opacity="0.9"/>
                <circle cx="80" cy="16" r="5" stroke="none"/>
                <path d="M72 36 Q70 22 80 21 Q90 22 88 36 Z" stroke="none"/>
                <path d="M76 13 Q80 3 89 9 L85 13 Z" stroke="none"/>
                <circle cx="90" cy="9.5" r="2" stroke="none"/>
                <path d="M30 24 Q30 46 50 47 L100 47 Q113 47 113 35 L113 28 L99 28 L96 38 L56 38 Q45 38 44 24 Z" stroke="none"/>
                <path d="M18 56 L106 56 Q120 56 119 45 M46 47 L43 56 M95 47 L98 56 M18 56 Q12 56 13 51" fill="none" stroke-width="2.2"/>
            </g>
        </svg>

        <!-- Colinas de neve com pinheiros (o texto do rodapé continua legível: neve translúcida) -->
        <svg class="season-hills" viewBox="0 0 600 120" preserveAspectRatio="none" aria-hidden="true">
            <path d="M0 78 Q120 40 260 70 T600 54 L600 120 L0 120 Z" fill="rgba(255,255,255,0.10)"/>
            <path d="M0 96 Q160 64 330 88 T600 80 L600 120 L0 120 Z" fill="rgba(255,255,255,0.13)"/>
        </svg>
        <svg class="season-trees" viewBox="0 0 160 120" aria-hidden="true">
            <g v-for="tree in trees" :key="tree.x" :transform="`translate(${tree.x} ${tree.y}) scale(${tree.s})`">
                <path d="M0 -60 L16 -30 L8 -30 L22 -6 L-22 -6 L-8 -30 L-16 -30 Z" fill="rgba(4,40,33,0.78)"/>
                <path d="M0 -60 L16 -30 L8 -30 L22 -6 L-22 -6 L-8 -30 L-16 -30 Z" fill="none" stroke="rgba(255,255,255,0.18)" stroke-width="1"/>
                <rect x="-3" y="-6" width="6" height="8" fill="rgba(4,40,33,0.85)"/>
                <circle v-for="(light, index) in tree.lights" :key="index" :cx="light[0]" :cy="light[1]" r="1.6"
                        class="season-tree-light" :style="{animationDelay: `${index * 0.4 + tree.x / 100}s`}"/>
            </g>
            <path d="M-2 0 L2.4 -3.4 L-3 -1.4 L3 -1.4 L-2.4 -3.4 Z" class="season-tree-star"
                  :transform="`translate(${trees[1].x} ${trees[1].y - 60 * trees[1].s - 2}) scale(2.6)`"/>
        </svg>

        <!-- Enfeites pendurados: balançam ao passar o mouse; quem clica nos quatro ganha um presente -->
        <div class="season-ornaments">
            <button v-for="(ornament, index) in ornaments" :key="ornament.id" ref="ornament" type="button"
                    class="season-ornament" :class="[`is-${ornament.kind}`, {'is-found': found.includes(index)}]"
                    :style="{left: ornament.left, '--string': `${ornament.length}px`, '--sway-delay': `${-index * 0.9}s`,
                             '--light': ornament.light, '--color': ornament.color, '--deep': ornament.deep}"
                    tabindex="-1" aria-hidden="true" @click="onOrnament(index, $event)" @mouseenter="nudge(index)">
                <span class="season-ornament-string"></span>
                <span class="season-ornament-ball">
                    <svg v-if="ornament.kind === 'star'" viewBox="-12 -12 24 24"><path :d="starPath"/></svg>
                </span>
            </button>
        </div>

        <transition name="season-gift">
            <div v-if="giftVisible" class="season-gift" role="status">
                <span class="season-gift-icon" aria-hidden="true"><i class="icon-gift"></i></span>
                <span class="season-gift-texts">
                    <strong>{{ greeting }}!</strong>
                    <span>Que {{ newYear ? 'o novo ano traga' : 'as festas tragam' }} muitas conquistas.
                        Com carinho, equipe R2 Soft.</span>
                </span>
            </div>
        </transition>
    </div>
</template>

<script>
// Cena de festas do painel esmeralda do login. Natal: lua, neve com bokeh, trenó dourado com rastro de faíscas,
// colinas de neve com pinheiros iluminados e quatro enfeites pendurados (achar os quatro dá um presente).
// Réveillon: fogos contínuos atrás do texto, confete, medalhas e estrelas douradas no lugar das bolas.
// O elemento pai precisa de position e overflow: hidden. Com "reduzir movimento", tudo fica parado.
import {mapState} from 'vuex';
import SeasonSky from '@/components/season/SeasonSky.vue';
import seasonBurst from '@/Helpers/global/seasonBurst';
import {createFireworks} from '@/Helpers/global/seasonFireworks';
import {prefersReducedMotion, seasonGreeting, seasonIntensity} from '@/Helpers/global/season';
import {STAR_PATH} from '@/Helpers/global/seasonShapes';

const FLIGHT_EVERY = 17;   // segundos entre um voo e outro
const FLIGHT_FIRST = 1.8;  // primeiro voo, logo depois da entrada da tela
const FLIGHT_TIME = 7.5;   // duração da travessia

const rand = (min, max) => min + Math.random() * (max - min);

function bezier(points, t) {
    const [p0, p1, p2, p3] = points;
    const u = 1 - t;
    return {
        x: u * u * u * p0.x + 3 * u * u * t * p1.x + 3 * u * t * t * p2.x + t * t * t * p3.x,
        y: u * u * u * p0.y + 3 * u * u * t * p1.y + 3 * u * t * t * p2.y + t * t * t * p3.y,
    };
}

export default {
    name: 'SeasonLoginScene',
    components: {SeasonSky},
    data() {
        return {
            starPath: STAR_PATH,
            reduced: prefersReducedMotion(),
            found: [],
            giftVisible: false,
            // Neve mais forte por alguns segundos quando o presente aparece
            flurry: 1,
            trees: [
                {x: 104, y: 112, s: 0.78, lights: [[-6, -22], [7, -14], [-10, -10], [3, -36]]},
                {x: 128, y: 114, s: 1.05, lights: [[-8, -16], [9, -24], [-3, -40], [12, -10], [-13, -9]]},
                {x: 150, y: 116, s: 0.62, lights: [[5, -20], [-7, -12]]},
            ],
        };
    },
    computed: {
        ...mapState({
            now: 'seasonNow',
            newYear: 'seasonNewYear',
            phase: 'seasonPhase',
        }),
        greeting() {
            return seasonGreeting(this.now);
        },
        intensity() {
            return seasonIntensity(this.now);
        },
        // Paleta contida: dourado, esmeralda clara, rubi e champanhe; no Réveillon, medalhas e estrelas douradas
        ornaments() {
            return this.newYear ? [
                {id: 1, kind: 'ball', left: '0px', length: 70, light: '#fff4cf', color: '#e2b85c', deep: '#8a6420'},
                {id: 2, kind: 'star', left: '30px', length: 40},
                {id: 3, kind: 'ball', left: '60px', length: 128, light: '#ffffff', color: '#f1e2bd', deep: '#a8834a'},
                {id: 4, kind: 'star', left: '90px', length: 146},
            ] : [
                {id: 1, kind: 'ball', left: '0px', length: 70, light: '#f8e7b0', color: '#d4a64a', deep: '#7a5418'},
                {id: 2, kind: 'ball', left: '30px', length: 40, light: '#ffb1b9', color: '#c0263a', deep: '#5c0a16'},
                {id: 3, kind: 'star', left: '60px', length: 128},
                {id: 4, kind: 'ball', left: '90px', length: 146, light: '#a7f3d0', color: '#1f9d7a', deep: '#06362d'},
            ];
        },
    },
    watch: {
        // Meia-noite do dia 31 com o login aberto: salva de fogos dos dois lados
        phase(phase, previous) {
            if (phase === 'newYearDay' && previous === 'newYearEve') this.fireworks?.salvo(8, 1200);
        },
        newYear() {
            // Natal <-> Réveillon com o login aberto: troca o motor
            this.stop();
            this.found = [];
            this.$nextTick(() => this.start());
        },
    },
    mounted() {
        this.start();
    },
    beforeUnmount() {
        this.stop();
        clearTimeout(this.giftTimer);
        clearTimeout(this.flurryTimer);
    },
    methods: {
        start() {
            if (this.reduced) return;
            this.sparks = [];
            this.flight = null;
            this.nextFlight = FLIGHT_FIRST;
            this.flights = 0;
            this.last = performance.now();
            this.trailCtx = this.$refs.trail.getContext('2d');
            this.resize = this.resize.bind(this);
            this.tick = this.tick.bind(this);
            this.onVisibility = this.onVisibility.bind(this);
            this.resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(this.resize) : null;
            this.resizeObserver?.observe(this.$el);
            document.addEventListener('visibilitychange', this.onVisibility);
            if (this.newYear && this.$refs.fireworks) {
                this.fireworks = createFireworks(this.$refs.fireworks, {auto: true, rate: 0.7});
            }
            this.resize();
            this.frame = requestAnimationFrame(this.tick);
        },
        stop() {
            cancelAnimationFrame(this.frame);
            this.resizeObserver?.disconnect();
            this.resizeObserver = null;
            if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
            this.fireworks?.destroy();
            this.fireworks = null;
        },
        resize() {
            const canvas = this.$refs.trail;
            if (!canvas || !this.trailCtx) return;
            const ratio = Math.min(window.devicePixelRatio || 1, 2);
            this.width = this.$el.clientWidth;
            this.height = this.$el.clientHeight;
            canvas.width = Math.round(this.width * ratio);
            canvas.height = Math.round(this.height * ratio);
            this.trailCtx.setTransform(ratio, 0, 0, ratio, 0, 0);
            this.fireworks?.resize();
        },
        onVisibility() {
            cancelAnimationFrame(this.frame);
            if (!document.hidden) {
                this.last = performance.now();
                this.frame = requestAnimationFrame(this.tick);
            }
        },
        // Trajeto do trenó: entra por um lado, cruza o alto do céu (acima do título) e sai pelo outro, subindo
        startFlight() {
            const w = this.width;
            const h = this.height;
            const toLeft = this.flights % 2 === 1;
            this.flights += 1;
            const points = [
                {x: -0.22 * w, y: rand(0.17, 0.24) * h},
                {x: 0.3 * w, y: rand(0, 0.05) * h},
                {x: 0.62 * w, y: rand(0.12, 0.18) * h},
                {x: 1.22 * w, y: rand(-0.02, 0.04) * h},
            ];
            // Volta: o mesmo desenho espelhado, entrando pela direita
            if (toLeft) points.forEach(point => (point.x = w - point.x));
            this.flight = {points, toLeft, t: 0, duration: FLIGHT_TIME * Math.max(0.65, Math.min(1, w / 620))};
        },
        updateFlight(dt) {
            const sleigh = this.$refs.sleigh;
            if (!sleigh) return;
            if (!this.flight) {
                this.nextFlight -= dt;
                if (this.nextFlight <= 0) this.startFlight();
                sleigh.style.opacity = '0';
                return;
            }
            const flight = this.flight;
            flight.t += dt / flight.duration;
            if (flight.t >= 1) {
                this.flight = null;
                this.nextFlight = FLIGHT_EVERY - FLIGHT_TIME;
                sleigh.style.opacity = '0';
                return;
            }
            const point = bezier(flight.points, flight.t);
            const ahead = bezier(flight.points, Math.min(1, flight.t + 0.01));
            let angle = Math.atan2(ahead.y - point.y, ahead.x - point.x) * 180 / Math.PI;
            if (flight.toLeft) angle += 180;
            const size = Math.max(110, Math.min(170, this.width * 0.26));
            // Leve sobe e desce, como galope no ar
            const bob = Math.sin(flight.t * 40) * 1.5;
            sleigh.style.width = `${size}px`;
            sleigh.style.opacity = '1';
            sleigh.style.transform = `translate(${point.x - size / 2}px, ${point.y - size * 0.11 + bob}px) rotate(${angle * 0.6}deg) scaleX(${flight.toLeft ? -1 : 1})`;
            // Faíscas saindo da traseira do trenó
            const back = flight.toLeft ? point.x + size * 0.42 : point.x - size * 0.42;
            for (let i = 0; i < 3; i++) {
                this.sparks.push({x: back + rand(-3, 3), y: point.y + size * 0.06 + rand(-3, 3), vx: rand(-8, 8), vy: rand(4, 22),
                    life: rand(0.8, 1.6), age: 0, size: rand(1, 2.4)});
            }
        },
        drawSparks(dt) {
            const ctx = this.trailCtx;
            ctx.clearRect(0, 0, this.width, this.height);
            ctx.globalCompositeOperation = 'lighter';
            this.sparks = this.sparks.filter(spark => {
                spark.age += dt;
                if (spark.age >= spark.life) return false;
                spark.x += spark.vx * dt;
                spark.y += spark.vy * dt;
                const fade = 1 - spark.age / spark.life;
                ctx.globalAlpha = fade * (Math.random() > 0.15 ? 1 : 0.4);
                ctx.fillStyle = spark.age < 0.15 ? '#fff8e1' : '#f3d68a';
                ctx.beginPath();
                ctx.arc(spark.x, spark.y, spark.size * (0.4 + fade * 0.6), 0, Math.PI * 2);
                ctx.fill();
                return true;
            });
            ctx.globalAlpha = 1;
            ctx.globalCompositeOperation = 'source-over';
        },
        tick(now) {
            const dt = Math.min(0.05, (now - this.last) / 1000);
            this.last = now;
            if (!this.newYear) this.updateFlight(dt);
            this.drawSparks(dt);
            this.frame = requestAnimationFrame(this.tick);
        },
        // Mouse por cima: o enfeite dá um balanço curto
        nudge(index) {
            const element = this.$refs.ornament?.[index];
            if (!element?.animate || this.reduced) return;
            element.animate([
                {transform: 'rotate(0deg)'}, {transform: 'rotate(9deg)'}, {transform: 'rotate(-6deg)'},
                {transform: 'rotate(3deg)'}, {transform: 'rotate(0deg)'},
            ], {duration: 1200, easing: 'ease-out', composite: 'add'});
        },
        onOrnament(index, event) {
            const element = this.$refs.ornament?.[index];
            if (element?.animate && !this.reduced) {
                element.animate([
                    {transform: 'rotate(0deg)'}, {transform: 'rotate(-22deg)'}, {transform: 'rotate(16deg)'},
                    {transform: 'rotate(-9deg)'}, {transform: 'rotate(4deg)'}, {transform: 'rotate(0deg)'},
                ], {duration: 1600, easing: 'ease-out', composite: 'add'});
            }
            const ball = event.currentTarget.querySelector('.season-ornament-ball');
            seasonBurst(ball, {newYear: this.newYear, origin: {x: 0.5, y: 0.6}, amount: 14});
            if (this.giftVisible || this.found.includes(index)) return;
            this.found.push(index);
            if (this.found.length < this.ornaments.length) return;
            // Os quatro: presente, chuva de neve (ou salva de fogos) e, depois de uns segundos, dá para achar de novo
            this.giftTimer = setTimeout(() => {
                this.giftVisible = true;
                this.flurry = 2.2;
                this.fireworks?.salvo(6, 700);
                this.flurryTimer = setTimeout(() => (this.flurry = 1), 4500);
                this.giftTimer = setTimeout(() => {
                    this.giftVisible = false;
                    this.found = [];
                }, 7000);
            }, 300);
        },
    },
};
</script>

<style>
.season-scene {
    position: absolute;
    inset: 0;
    overflow: hidden;
    pointer-events: none;
}

/* A cena não cria camada própria (sem z-index): o céu fica atrás do texto do painel (z-index 1) e os enfeites
   e o presente ficam na frente, para receber o clique */
.season-scene > * {
    z-index: 0;
}

.season-scene > .season-ornaments {
    z-index: 2;
}

.season-scene > .season-gift {
    z-index: 3;
}

.season-scene-canvas {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
}

.season-moon {
    position: absolute;
    top: 118px;
    right: 30px;
    width: 96px;
    height: 96px;
    border-radius: 50%;
    background:
        radial-gradient(circle at 36% 34%, #fffdf4 0%, #fdf3d4 42%, #f0dca8 72%, #e2c47f 100%);
    box-shadow:
        0 0 40px 10px rgba(255, 244, 207, 0.32),
        0 0 120px 40px rgba(243, 214, 138, 0.16);
    opacity: 0.92;
}

/* Crateras bem suaves */
.season-moon::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background:
        radial-gradient(circle at 62% 38%, rgba(210, 180, 120, 0.35) 0 7px, transparent 8px),
        radial-gradient(circle at 40% 66%, rgba(210, 180, 120, 0.28) 0 10px, transparent 11px),
        radial-gradient(circle at 70% 70%, rgba(210, 180, 120, 0.22) 0 5px, transparent 6px);
}

.season-sleigh {
    position: absolute;
    left: 0;
    top: 0;
    width: 150px;
    opacity: 0;
    transform-origin: 50% 50%;
    filter: drop-shadow(0 0 6px rgba(243, 214, 138, 0.75)) drop-shadow(0 0 18px rgba(243, 214, 138, 0.3));
    will-change: transform;
}

.season-reindeer-legs {
    animation: season-gallop 0.5s ease-in-out infinite alternate;
    transform-box: fill-box;
    transform-origin: 50% 0;
}

@keyframes season-gallop {
    from { transform: scaleY(0.82) skewX(-8deg); }
    to { transform: scaleY(1) skewX(8deg); }
}

.season-hills {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 120px;
}

.season-trees {
    position: absolute;
    right: 10px;
    bottom: 0;
    width: 170px;
    height: 128px;
}

.season-tree-light {
    fill: #f6c453;
    filter: drop-shadow(0 0 2px rgba(246, 196, 83, 0.9));
    animation: season-tree-twinkle 2.6s ease-in-out infinite;
}

.season-tree-light:nth-of-type(2n) {
    fill: #ff7a88;
    filter: drop-shadow(0 0 2px rgba(255, 122, 136, 0.9));
}

.season-tree-light:nth-of-type(3n) {
    fill: #fff1cf;
}

.season-tree-star {
    fill: #f3d68a;
    filter: drop-shadow(0 0 3px rgba(243, 214, 138, 1));
    animation: season-star-breathe 3s ease-in-out infinite;
    transform-box: fill-box;
    transform-origin: 50% 50%;
}

@keyframes season-tree-twinkle {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.35; }
}

.season-ornaments {
    position: absolute;
    top: 0;
    right: 26px;
    width: 92px;
    height: 0;
}

.season-ornament {
    position: absolute;
    top: 0;
    width: 30px;
    margin-left: -15px;
    padding: 0;
    border: 0;
    background: transparent;
    transform-origin: 50% 0;
    animation: season-ornament-sway 5.5s ease-in-out var(--sway-delay) infinite alternate;
    pointer-events: auto;
    cursor: pointer;
    outline: none;
}

.season-ornament-string {
    display: block;
    width: 1px;
    height: var(--string);
    margin: 0 auto;
    background: linear-gradient(rgba(243, 214, 138, 0.15), rgba(243, 214, 138, 0.8));
}

.season-ornament-ball {
    position: relative;
    display: block;
    width: 30px;
    height: 30px;
    margin-top: 4px;
    border-radius: 50%;
    background:
        radial-gradient(circle at 34% 30%, var(--light) 0%, var(--color) 48%, var(--deep) 100%);
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.28), inset -3px -4px 8px rgba(0, 0, 0, 0.25);
    transition: transform 0.3s cubic-bezier(0.3, 1.6, 0.5, 1), box-shadow 0.3s ease;
}

/* Tampinha dourada da bola */
.season-ornament-ball::before {
    content: "";
    position: absolute;
    top: -5px;
    left: 50%;
    width: 9px;
    height: 6px;
    margin-left: -4.5px;
    border-radius: 2px 2px 1px 1px;
    background: linear-gradient(90deg, #8a6420, #f3d68a 50%, #8a6420);
}

/* Reflexo */
.season-ornament-ball::after {
    content: "";
    position: absolute;
    top: 6px;
    left: 7px;
    width: 9px;
    height: 5px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.55);
    transform: rotate(-30deg);
    filter: blur(0.5px);
}

.season-ornament.is-star .season-ornament-ball {
    background: none;
    box-shadow: none;
}

.season-ornament.is-star .season-ornament-ball::before,
.season-ornament.is-star .season-ornament-ball::after {
    display: none;
}

.season-ornament.is-star svg {
    width: 34px;
    height: 34px;
    margin: -2px 0 0 -2px;
    fill: #f3d68a;
    filter: drop-shadow(0 0 8px rgba(243, 214, 138, 0.85));
}

.season-ornament:hover .season-ornament-ball {
    transform: scale(1.1);
}

/* Enfeite já encontrado: acende com um halo dourado */
.season-ornament.is-found .season-ornament-ball {
    box-shadow: 0 0 0 2px rgba(243, 214, 138, 0.9), 0 0 22px 4px rgba(243, 214, 138, 0.55),
        inset -3px -4px 8px rgba(0, 0, 0, 0.2);
}

.season-ornament.is-star.is-found svg {
    filter: drop-shadow(0 0 14px rgba(255, 244, 207, 1));
}

@keyframes season-ornament-sway {
    from { transform: rotate(-3deg); }
    to { transform: rotate(3deg); }
}

.season-gift {
    position: absolute;
    left: 50%;
    bottom: 128px;
    width: min(420px, calc(100% - 40px));
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px 18px;
    border-radius: 18px;
    background: linear-gradient(135deg, rgba(255, 244, 207, 0.97), rgba(243, 214, 138, 0.95));
    color: #3d2c0b;
    box-shadow: 0 24px 50px rgba(0, 0, 0, 0.3);
}

.season-gift-icon {
    flex: 0 0 auto;
    width: 46px;
    height: 46px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 14px;
    background: #0e5e4e;
    color: #f3d68a;
    font-size: 22px;
    animation: season-gift-wiggle 1s ease-in-out 0.3s 2;
}

.season-gift-texts {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 13px;
    line-height: 1.45;
}

.season-gift-texts strong {
    font-size: 16px;
    font-weight: 800;
    color: #2b1f07;
}

@keyframes season-gift-wiggle {
    0%, 100% { transform: rotate(0); }
    25% { transform: rotate(-12deg) scale(1.08); }
    75% { transform: rotate(10deg) scale(1.08); }
}

.season-gift-enter-active,
.season-gift-leave-active {
    transition: opacity 0.4s ease, transform 0.4s cubic-bezier(0.3, 1.4, 0.5, 1);
}

.season-gift-enter-from,
.season-gift-leave-to {
    opacity: 0;
    transform: translate(-50%, 16px) scale(0.96);
}

/* Réveillon: céu azul-noite por cima do esmeralda */
.season-scene.is-new-year {
    background:
        radial-gradient(circle at 80% 10%, rgba(243, 214, 138, 0.18), transparent 40%),
        linear-gradient(170deg, rgba(11, 26, 51, 0.78), rgba(11, 26, 51, 0.35) 60%, rgba(6, 54, 45, 0));
}

.season-scene.is-new-year .season-ornament-string {
    background: linear-gradient(rgba(255, 244, 207, 0.15), rgba(255, 244, 207, 0.85));
}

/* Painel empilhado (tablet e celular): o título ocupa a largura toda, então os enfeites saem e a lua vai
   para o canto */
@media (max-width: 991.98px) {
    .season-ornaments {
        display: none;
    }

    .season-moon {
        width: 54px;
        height: 54px;
        top: 22px;
        right: 22px;
    }
}

@media (max-width: 575.98px) {
    .season-moon {
        display: none;
    }

    .season-trees {
        width: 120px;
        height: 90px;
    }

    .season-gift {
        bottom: 24px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .season-ornament,
    .season-tree-light,
    .season-tree-star,
    .season-reindeer-legs,
    .season-gift-icon {
        animation: none !important;
    }
}
</style>
